type DateRangePickerElements = {
  picker: HTMLElement;
  trigger: HTMLElement;
  panel: HTMLElement;
  btnToggle: HTMLButtonElement;
  btnApplyRange: HTMLElement;
  btnResetRange: HTMLElement;
  inputFrom: HTMLInputElement;
  inputTo: HTMLInputElement;
  displayInput: HTMLInputElement;
  monthLabel: HTMLElement;
  daysGrid: HTMLElement;
  btnPrev: HTMLButtonElement;
  btnNext: HTMLButtonElement;
};

const DEFAULT_PLACEHOLDER = 'дд.мм.гггг - дд.мм.гггг';
const INIT_FLAG = 'rangePickerBound';

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function formatDate(value: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODate(value: string): Date | null {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBefore(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() < startOfDay(b).getTime();
}

function isInRange(day: Date, from: Date, to: Date): boolean {
  const d = startOfDay(day).getTime();
  const start = startOfDay(from).getTime();
  const end = startOfDay(to).getTime();

  return d >= Math.min(start, end) && d <= Math.max(start, end);
}

function applyRange(
  inputFrom: HTMLInputElement,
  inputTo: HTMLInputElement,
  displayInput: HTMLInputElement,
  emptyPlaceholder = DEFAULT_PLACEHOLDER,
): void {
  const from = inputFrom.value;
  const to = inputTo.value;
  const fromFormatted = formatDate(from);
  const toFormatted = formatDate(to);

  if (from && to) {
    displayInput.value = `${fromFormatted} - ${toFormatted}`;
  } else if (from) {
    displayInput.value = `${fromFormatted} - …`;
  } else {
    displayInput.value = '';
    displayInput.placeholder = emptyPlaceholder;
  }
}

function parseDisplayDate(value: string): string | null {
  if (!value || value === '…') return null;

  const parts = value.split('.');

  if (parts.length !== 3) return null;

  const [day, month, year] = parts;

  if (!day || !month || !year) return null;

  const iso = `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

  return parseISODate(iso) ? iso : null;
}

function parseDisplayRange(value: string): { from: string | null; to: string | null } {
  const trimmed = value.trim();

  if (!trimmed) {
    return { from: null, to: null };
  }

  const parts = trimmed.split(/\s*[-–—]\s*/);
  const from = parseDisplayDate(parts[0]?.trim() ?? '');
  const toPart = parts[1]?.trim();
  const to = toPart && toPart !== '…' ? parseDisplayDate(toPart) : null;

  return { from, to };
}

function resolveRoot(root?: ParentNode): ParentNode {
  return root ?? document;
}

export function queryDateRangePicker(root?: ParentNode): DateRangePickerElements | null {
  const scope = resolveRoot(root);
  const picker = scope.querySelector<HTMLElement>('.date-range-picker');
  const trigger = picker?.querySelector<HTMLElement>('[data-range-trigger]') ?? null;
  const panel = picker?.querySelector<HTMLElement>('[data-range-panel]') ?? null;
  const btnToggle = picker?.querySelector<HTMLButtonElement>('[data-range-toggle]') ?? null;
  const btnApplyRange = picker?.querySelector<HTMLElement>('[data-range-apply]') ?? null;
  const btnResetRange = picker?.querySelector<HTMLElement>('[data-range-reset]') ?? null;
  const inputFrom = picker?.querySelector<HTMLInputElement>('[data-range-from]') ?? null;
  const inputTo = picker?.querySelector<HTMLInputElement>('[data-range-to]') ?? null;
  const displayInput = picker?.querySelector<HTMLInputElement>('[data-range-display]') ?? null;
  const monthLabel = picker?.querySelector<HTMLElement>('[data-range-month-label]') ?? null;
  const daysGrid = picker?.querySelector<HTMLElement>('[data-range-days]') ?? null;
  const btnPrev = picker?.querySelector<HTMLButtonElement>('[data-range-prev]') ?? null;
  const btnNext = picker?.querySelector<HTMLButtonElement>('[data-range-next]') ?? null;

  if (
    !picker ||
    !trigger ||
    !panel ||
    !btnToggle ||
    !btnApplyRange ||
    !btnResetRange ||
    !inputFrom ||
    !inputTo ||
    !displayInput ||
    !monthLabel ||
    !daysGrid ||
    !btnPrev ||
    !btnNext
  ) {
    return null;
  }

  return {
    picker,
    trigger,
    panel,
    btnToggle,
    btnApplyRange,
    btnResetRange,
    inputFrom,
    inputTo,
    displayInput,
    monthLabel,
    daysGrid,
    btnPrev,
    btnNext,
  };
}

function initCalendar(elements: DateRangePickerElements, emptyPlaceholder: string): () => void {
  const {
    picker,
    inputFrom,
    inputTo,
    displayInput,
    monthLabel,
    daysGrid,
    btnPrev,
    btnNext,
    btnResetRange,
    btnApplyRange,
    panel,
  } = elements;

  const buttonRow = panel.querySelector<HTMLElement>('.button-row');

  const today = startOfDay(new Date());
  let viewDate = new Date(today.getFullYear(), today.getMonth(), 1);
  let draftFrom: Date | null = null;
  let draftTo: Date | null = null;
  let skipRevertOnClose = false;

  const syncDraftFromInputs = (): void => {
    draftFrom = parseISODate(inputFrom.value);
    draftTo = parseISODate(inputTo.value);
    if (draftFrom) {
      viewDate = new Date(draftFrom.getFullYear(), draftFrom.getMonth(), 1);
    }
  };

  const syncDraftOnOpen = (): void => {
    if (inputFrom.value) {
      syncDraftFromInputs();
      return;
    }

    const { from, to } = parseDisplayRange(displayInput.value);
    draftFrom = from ? parseISODate(from) : null;
    draftTo = to ? parseISODate(to) : null;

    if (draftFrom) {
      viewDate = new Date(draftFrom.getFullYear(), draftFrom.getMonth(), 1);
    }
  };

  const updateDisplayFromDraft = (): void => {
    if (draftFrom && draftTo) {
      displayInput.value = `${formatDate(toISODate(draftFrom))} - ${formatDate(toISODate(draftTo))}`;
    } else if (draftFrom) {
      displayInput.value = `${formatDate(toISODate(draftFrom))} - …`;
    } else if (!inputFrom.value && !inputTo.value) {
      displayInput.value = '';
      displayInput.placeholder = emptyPlaceholder;
    }
  };

  const clearDraft = (): void => {
    draftFrom = null;
    draftTo = null;
    inputFrom.value = '';
    inputTo.value = '';
    applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
    render();
  };

  const commitDraftToInputs = (): void => {
    inputFrom.value = draftFrom ? toISODate(draftFrom) : '';
    inputTo.value = draftTo ? toISODate(draftTo) : '';
    applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
  };

  const onDayClick = (date: Date): void => {
    const day = startOfDay(date);

    if (!draftFrom || (draftFrom && draftTo)) {
      draftFrom = day;
      draftTo = null;
    } else if (isBefore(day, draftFrom)) {
      draftFrom = day;
      draftTo = null;
    } else {
      draftTo = day;
    }

    updateDisplayFromDraft();
    render();
  };

  const updateButtonRowVisibility = (): void => {
    buttonRow?.classList.toggle('is-visible', draftFrom !== null);
  };

  const render = (): void => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    monthLabel.textContent = `${MONTH_NAMES[month]}, ${year}`;
    updateButtonRowVisibility();

    const firstOfMonth = new Date(year, month, 1);
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cellCount = Math.ceil((startOffset + daysInMonth) / 7) * 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    daysGrid.replaceChildren();

    for (let i = 0; i < cellCount; i += 1) {
      const cellDate = new Date(
        gridStart.getFullYear(),
        gridStart.getMonth(),
        gridStart.getDate() + i,
      );
      const day = startOfDay(cellDate);
      const isOtherMonth = day.getMonth() !== month;
      const rangeEnd = draftTo ?? draftFrom;
      const inRange = draftFrom && rangeEnd ? isInRange(day, draftFrom, rangeEnd) : false;
      const isStart = draftFrom ? isSameDay(day, draftFrom) : false;
      const isEnd = rangeEnd ? isSameDay(day, rangeEnd) : false;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'date-range-day';
      button.setAttribute('role', 'gridcell');
      button.setAttribute('aria-label', day.toLocaleDateString('ru-RU'));

      if (isOtherMonth) button.classList.add('is-other-month');
      if (isSameDay(day, today)) button.classList.add('is-today');
      if (inRange && !isStart && !isEnd) button.classList.add('is-in-range');
      if (isStart) {
        button.classList.add('is-range-start');
        if (!draftTo) button.classList.add('is-range-start-only');
      }
      if (isEnd && draftTo) button.classList.add('is-range-end');

      const inner = document.createElement('span');
      inner.className = 'date-range-day-inner';
      inner.textContent = String(day.getDate());
      button.append(inner);

      button.addEventListener('click', () => onDayClick(day));
      daysGrid.append(button);
    }
  };

  const onPrevMonth = (): void => {
    viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
    render();
  };

  const onNextMonth = (): void => {
    viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    render();
  };

  const dispatchRangeChange = (): void => {
    picker.dispatchEvent(new CustomEvent('date-range-change', { bubbles: true }));
  };

  const onResetClick = (): void => {
    clearDraft();
    dispatchRangeChange();
  };

  const onApplyClick = (): void => {
    commitDraftToInputs();
    skipRevertOnClose = true;
    picker.classList.remove('open');
    dispatchRangeChange();
  };

  const onOpen = (): void => {
    syncDraftOnOpen();
    render();
  };

  const onClose = (): void => {
    const keepCommittedRange = skipRevertOnClose || picker.dataset.rangeCommitOnClose === 'true';

    skipRevertOnClose = false;
    delete picker.dataset.rangeCommitOnClose;

    if (keepCommittedRange) {
      return;
    }

    syncDraftFromInputs();
    applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
    render();
  };

  btnPrev.addEventListener('click', onPrevMonth);
  btnNext.addEventListener('click', onNextMonth);
  btnResetRange.addEventListener('click', onResetClick);
  btnApplyRange.addEventListener('click', onApplyClick);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName !== 'class') continue;

      const hadOpen = mutation.oldValue?.split(/\s+/).includes('open') ?? false;
      const hasOpen = picker.classList.contains('open');

      if (hasOpen && !hadOpen) {
        onOpen();
      } else if (!hasOpen && hadOpen) {
        onClose();
      }
    }
  });
  observer.observe(picker, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });

  const onRangeSync = (): void => {
    syncDraftFromInputs();
    render();
  };

  picker.addEventListener('range-sync', onRangeSync);
  picker.addEventListener('range-before-close', onClose);

  syncDraftFromInputs();
  render();

  return () => {
    observer.disconnect();
    picker.removeEventListener('range-sync', onRangeSync);
    picker.removeEventListener('range-before-close', onClose);
  };
}

export function initDateRangePicker(
  root?: ParentNode,
  options?: { emptyPlaceholder?: string },
): DateRangePickerElements | null {
  const elements = queryDateRangePicker(root);
  if (!elements) return null;

  const { picker, panel, btnToggle, displayInput } = elements;

  if (picker.dataset[INIT_FLAG] === 'true') {
    return elements;
  }
  picker.dataset[INIT_FLAG] = 'true';

  const emptyPlaceholder = options?.emptyPlaceholder ?? DEFAULT_PLACEHOLDER;

  const dispatchRangeChange = (): void => {
    picker.dispatchEvent(new CustomEvent('date-range-change', { bubbles: true }));
  };

  initCalendar(elements, emptyPlaceholder);

  const commitDisplayInput = (): void => {
    const { inputFrom, inputTo } = elements;
    const raw = displayInput.value.trim();

    if (!raw) {
      if (picker.classList.contains('open')) {
        return;
      }

      inputFrom.value = '';
      inputTo.value = '';
      applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
      picker.dispatchEvent(new Event('range-sync'));
      dispatchRangeChange();
      return;
    }

    const { from, to } = parseDisplayRange(raw);

    if (!from) {
      applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
      return;
    }

    inputFrom.value = from;
    inputTo.value = to ?? '';
    applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
    picker.dispatchEvent(new Event('range-sync'));
    dispatchRangeChange();
  };

  const openPicker = (): void => {
    picker.classList.add('open');
  };

  const onOpenPickerClick = (e: MouseEvent): void => {
    e.stopPropagation();
    openPicker();
  };

  const onDisplayKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDisplayInput();
      picker.dataset.rangeCommitOnClose = 'true';
      picker.classList.remove('open');
      displayInput.blur();
    }
  };

  const onDisplayBlur = (): void => {
    if (picker.classList.contains('open')) {
      return;
    }

    commitDisplayInput();
  };

  const onDocumentMouseDown = (e: MouseEvent): void => {
    const target = e.target;
    if (!(target instanceof Node) || picker.contains(target)) {
      return;
    }

    if (picker.classList.contains('open')) {
      picker.dispatchEvent(new Event('range-before-close'));
    }
  };

  const onDocumentClick = (e: MouseEvent): void => {
    const target = e.target;
    if (!(target instanceof Node) || picker.contains(target)) {
      return;
    }

    picker.classList.remove('open');
  };

  const onPanelClick = (e: MouseEvent): void => {
    e.stopPropagation();
  };

  const onPanelMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
  };

  displayInput.addEventListener('click', onOpenPickerClick);
  btnToggle.addEventListener('click', (e) => {
    e.preventDefault();
    onOpenPickerClick(e);
  });
  displayInput.addEventListener('keydown', onDisplayKeydown);
  displayInput.addEventListener('blur', onDisplayBlur);
  document.addEventListener('mousedown', onDocumentMouseDown, true);
  document.addEventListener('click', onDocumentClick);
  panel.addEventListener('click', onPanelClick);
  panel.addEventListener('mousedown', onPanelMouseDown);

  return elements;
}

export function populateDateRangeFromURL(
  root?: ParentNode,
  emptyPlaceholder = DEFAULT_PLACEHOLDER,
): void {
  const elements = queryDateRangePicker(root);
  if (!elements) return;

  const { inputFrom, inputTo, displayInput, picker } = elements;
  const urlParams = new URLSearchParams(window.location.search);
  const dateFromParam = urlParams.get('date_from');
  const dateToParam = urlParams.get('date_to');

  if (dateFromParam) {
    const fromDate = parseDateFromURL(dateFromParam);
    if (fromDate) inputFrom.value = fromDate;
  }

  if (dateToParam) {
    const toDate = parseDateFromURL(dateToParam);
    if (toDate) inputTo.value = toDate;
  }

  if (!dateFromParam && !dateToParam) return;

  applyRange(inputFrom, inputTo, displayInput, emptyPlaceholder);
  picker.dispatchEvent(new Event('range-sync'));
}

function parseDateFromURL(dateString: string): string | null {
  if (!dateString) return null;

  const parts = dateString.split('.');

  if (parts.length !== 3) return null;

  const [day, month, year] = parts;

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

export { formatDate, applyRange, DEFAULT_PLACEHOLDER };
